import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FileBarChart, Download, Check, FileText, Table, BarChart3, Shield, ClipboardList, FolderOpen, Users, ChevronDown, ChevronUp } from 'lucide-react';
import api, { downloadBlob } from '../services/api';
import './ReportConstructor.css';

// Структура секций (без текстовых меток — они берутся из i18n)
const SECTION_DEFS = [
  { id: 'kpi',          icon: BarChart3,    formats: ['excel', 'pdf'] },
  { id: 'employee_kpi', icon: Users,        formats: [] },  // интерактивный просмотр
  { id: 'discipline',   icon: Shield,       formats: ['excel', 'pdf'] },
  { id: 'reports',      icon: FileText,     formats: ['excel'] },
  { id: 'documents',    icon: FolderOpen,   formats: ['excel'] },
  { id: 'plans',        icon: ClipboardList, formats: ['excel'] },
  { id: 'analytical',   icon: Table,        formats: ['word', 'pdf'] },
];

const ROLE_LABELS = {
  admin: 'Администратор',
  director: 'Директор',
  deputy_director: 'Зам. директора',
  department_head: 'Нач. отдела',
  employee: 'Сотрудник',
  analyst: 'Аналитик',
};

const kpiColor = (score) => {
  if (score >= 70) return '#10b981';
  if (score >= 40) return '#f59e0b';
  return '#ef4444';
};

const FORMAT_LABELS = {
  excel: { label: 'Excel', ext: 'xlsx' },
  pdf: { label: 'PDF', ext: 'pdf' },
  word: { label: 'Word', ext: 'docx' },
};

function ReportConstructor() {
  const { t } = useTranslation(['reports', 'common']);

  // Создаём секции с переведёнными метками внутри компонента
  const SECTIONS = SECTION_DEFS.map(def => ({
    ...def,
    label: t(`reports:constructor.sections.${def.id}`),
    description: t(`reports:constructor.sections.${def.id}Desc`),
  }));

  const [departments, setDepartments] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [format, setFormat] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [department, setDepartment] = useState('');
  const [downloading, setDownloading] = useState(false);

  // KPI сотрудников
  const [empKpiData, setEmpKpiData] = useState(null);
  const [empKpiLoading, setEmpKpiLoading] = useState(false);
  const [empKpiError, setEmpKpiError] = useState(null);
  const [empKpiDept, setEmpKpiDept] = useState('');
  const [empKpiExpanded, setEmpKpiExpanded] = useState(null);
  const [empKpiExporting, setEmpKpiExporting] = useState(null); // 'excel' | 'word' | null

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  useEffect(() => {
    api.getDepartments().then(res => {
      setDepartments(res.data.results || res.data);
    }).catch(() => {});
  }, []);

  const loadEmployeeKpi = async (params = {}) => {
    setEmpKpiLoading(true);
    setEmpKpiExpanded(null);
    setEmpKpiError(null);
    try {
      const res = await api.getEmployeeKPI(params);
      setEmpKpiData(res.data);
    } catch (e) {
      console.error('Ошибка загрузки KPI сотрудников:', e);
      setEmpKpiError('Не удалось загрузить данные. Проверьте что сервер запущен.');
      setEmpKpiData(null);
    } finally {
      setEmpKpiLoading(false);
    }
  };

  const handleShowEmployeeKpi = () => {
    const params = { year };
    if (empKpiDept) params.department_id = empKpiDept;
    loadEmployeeKpi(params);
  };

  const handleEmpKpiExport = async (fmt) => {
    setEmpKpiExporting(fmt);
    try {
      const params = { year };
      if (empKpiDept) params.department_id = empKpiDept;
      let res;
      if (fmt === 'excel') {
        res = await api.exportEmpKpiExcel(params);
      } else {
        res = await api.exportEmpKpiWord(params);
      }
      downloadBlob(res, `KPI_сотрудников_${year}.${fmt === 'excel' ? 'xlsx' : 'docx'}`);
    } catch (e) {
      alert('Ошибка экспорта: ' + (e.message || 'неизвестная ошибка'));
    } finally {
      setEmpKpiExporting(null);
    }
  };

  const handleSelectSection = (sectionId) => {
    setSelectedSection(sectionId);
    const section = SECTIONS.find(s => s.id === sectionId);
    if (section) {
      setFormat(section.formats[0] || '');
    }
  };

  const handleDownload = async () => {
    if (!selectedSection || !format) return;
    setDownloading(true);

    const params = { year };
    if (department) params.department = department;

    try {
      let response;
      let filename;

      switch (selectedSection) {
        case 'kpi':
          if (format === 'excel') {
            response = await api.exportKpiExcel(params);
            filename = `KPI_отделов_${year}.xlsx`;
          } else {
            response = await api.exportKpiPdf(params);
            filename = `KPI_отделов_${year}.pdf`;
          }
          break;
        case 'discipline':
          if (format === 'excel') {
            response = await api.exportDisciplineExcel(params);
            filename = `Дисциплина_${year}.xlsx`;
          } else {
            response = await api.exportDisciplinePdf(params);
            filename = `Дисциплина_${year}.pdf`;
          }
          break;
        case 'reports':
          response = await api.exportReportsExcel(params);
          filename = `Отчёты_${year}.xlsx`;
          break;
        case 'documents':
          response = await api.exportDocumentsExcel(params);
          filename = `Документы_${year}.xlsx`;
          break;
        case 'plans':
          response = await api.exportPlansExcel(params);
          filename = `Планы_${year}.xlsx`;
          break;
        case 'analytical':
          if (format === 'word') {
            response = await api.exportAnalyticalWord(params);
            filename = `Аналитическая_справка_${year}.docx`;
          } else {
            response = await api.exportAnalyticalPdf(params);
            filename = `Аналитическая_справка_${year}.pdf`;
          }
          break;
        default:
          break;
      }

      if (response) {
        downloadBlob(response, filename);
      }
    } catch (error) {
      console.error('Export error:', error);
      alert(t('reports:constructor.exportError'));
    } finally {
      setDownloading(false);
    }
  };

  const currentSection = SECTIONS.find(s => s.id === selectedSection);

  return (
    <div className="constructor-page">
      {/* Header */}
      <div className="constructor-header">
        <h1>
          <FileBarChart size={24} />
          {t('reports:pageTitles.reportConstructor')}
        </h1>
      </div>

      <div className={`constructor-layout${selectedSection === 'employee_kpi' ? ' employee-kpi-mode' : ''}`}>
        {/* Левая панель — выбор раздела */}
        <div className="constructor-sections">
          <div className="constructor-card">
            <h2>{t('reports:constructor.selectSection')}</h2>
            <div className="sections-list">
              {SECTIONS.map(section => {
                const Icon = section.icon;
                return (
                  <div
                    key={section.id}
                    className={`section-item ${selectedSection === section.id ? 'active' : ''}`}
                    onClick={() => handleSelectSection(section.id)}
                  >
                    <div className="section-icon">
                      <Icon size={20} />
                    </div>
                    <div className="section-info">
                      <span className="section-label">{section.label}</span>
                      <span className="section-desc">{section.description}</span>
                    </div>
                    {selectedSection === section.id && (
                      <Check size={18} className="section-check" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Правая панель — параметры и скачивание */}
        <div className="constructor-params">
          {selectedSection === 'employee_kpi' ? (
            /* ── Интерактивный раздел KPI сотрудников ── */
            <div className="constructor-card" style={{ maxWidth: '100%' }}>
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Users size={20} /> KPI сотрудников
              </h2>

              {/* Фильтры */}
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'flex-end' }}>
                <div className="param-group" style={{ margin: 0, flex: '0 0 auto' }}>
                  <label>{t('reports:constructor.year')}</label>
                  <select className="param-control" value={year} onChange={e => setYear(parseInt(e.target.value))}>
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div className="param-group" style={{ margin: 0, flex: '1 1 200px' }}>
                  <label>{t('reports:constructor.department')}</label>
                  <select className="param-control" value={empKpiDept} onChange={e => setEmpKpiDept(e.target.value)}>
                    <option value="">{t('reports:constructor.allDepartments')}</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name_ru}</option>)}
                  </select>
                </div>
                <div className="param-group" style={{ margin: 0 }}>
                  <button
                    onClick={handleShowEmployeeKpi}
                    disabled={empKpiLoading}
                    style={{
                      padding: '0.6rem 1.25rem',
                      background: empKpiLoading ? '#94a3b8' : 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: empKpiLoading ? 'not-allowed' : 'pointer',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                    }}
                  >
                    {empKpiLoading ? '⏳ Загрузка...' : '📊 Показать'}
                  </button>
                </div>
              </div>

              {/* Ошибка */}
              {empKpiError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '0.75rem 1rem', color: '#dc2626', marginBottom: '1rem', fontSize: '0.9rem' }}>
                  ⚠️ {empKpiError}
                </div>
              )}

              {/* Сводные карточки */}
              {empKpiData && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
                    {[
                      { label: 'Всего', value: empKpiData.total, color: '#1e88e5' },
                      { label: 'KPI ≥ 70', value: empKpiData.employees.filter(e => e.kpi_score >= 70).length, color: '#10b981' },
                      { label: 'KPI 40–69', value: empKpiData.employees.filter(e => e.kpi_score >= 40 && e.kpi_score < 70).length, color: '#f59e0b' },
                      { label: 'KPI < 40', value: empKpiData.employees.filter(e => e.kpi_score < 40).length, color: '#ef4444' },
                    ].map(card => (
                      <div key={card.label} style={{
                        background: '#f8fafc', borderRadius: '8px', padding: '0.75rem',
                        borderLeft: `4px solid ${card.color}`, textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: card.color }}>{card.value}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{card.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Таблица */}
                  <div style={{ overflowX: 'auto' }}>
                    <table className="constructor-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Сотрудник</th>
                          <th>Отдел</th>
                          <th>KPI</th>
                          <th>Отчёты</th>
                          <th>Поручения</th>
                          <th>Задачи</th>
                          <th>Посещ.</th>
                          <th>Актив.</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {empKpiData.employees.map(emp => (
                          <React.Fragment key={emp.user_id}>
                            <tr style={{ cursor: 'pointer' }} onClick={() => setEmpKpiExpanded(empKpiExpanded === emp.user_id ? null : emp.user_id)}>
                              <td><span className={`rank-num ${emp.rank <= 3 ? 'top' : ''}`}>{emp.rank}</span></td>
                              <td>
                                <div style={{ fontWeight: 600 }}>{emp.full_name}</div>
                                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{ROLE_LABELS[emp.role] || emp.role}</div>
                              </td>
                              <td style={{ fontSize: '0.78rem', color: '#64748b' }}>{emp.department_name}</td>
                              <td>
                                <div className="kpi-bar-cell">
                                  <div className="kpi-bar-bg">
                                    <div className="kpi-bar-fill" style={{ width: `${Math.min(emp.kpi_score, 100)}%`, background: kpiColor(emp.kpi_score) }} />
                                  </div>
                                  <span style={{ fontWeight: 700, color: kpiColor(emp.kpi_score), minWidth: '32px' }}>{emp.kpi_score}</span>
                                </div>
                              </td>
                              <td style={{ fontSize: '0.78rem' }}>
                                {emp.reports.total > 0 ? `${emp.reports.total} (${emp.reports.approval_rate}%)` : <span style={{ color: '#cbd5e1' }}>—</span>}
                              </td>
                              <td style={{ fontSize: '0.78rem' }}>
                                {emp.documents.total > 0
                                  ? <span>{emp.documents.total} ({emp.documents.completion_rate}%){emp.documents.overdue > 0 && <span style={{ color: '#ef4444' }}> ⚠{emp.documents.overdue}</span>}</span>
                                  : <span style={{ color: '#cbd5e1' }}>—</span>}
                              </td>
                              <td style={{ fontSize: '0.78rem' }}>
                                {emp.tasks.total > 0
                                  ? <span>{emp.tasks.avg_completion}%{emp.tasks.carry_over > 0 && <span style={{ color: '#f59e0b' }}> ({emp.tasks.carry_over}↩)</span>}</span>
                                  : <span style={{ color: '#cbd5e1' }}>—</span>}
                              </td>
                              <td style={{ fontSize: '0.78rem' }}>
                                {emp.attendance.total_days > 0
                                  ? <span style={{ color: kpiColor(emp.attendance.attendance_rate) }}>{emp.attendance.attendance_rate}%</span>
                                  : <span style={{ color: '#cbd5e1' }}>—</span>}
                              </td>
                              <td>
                                <span style={{ padding: '2px 6px', borderRadius: '10px', fontSize: '0.75rem', background: '#e0f2fe', color: '#0369a1' }}>
                                  {emp.activity_count}
                                </span>
                              </td>
                              <td>{empKpiExpanded === emp.user_id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</td>
                            </tr>
                            {empKpiExpanded === emp.user_id && (
                              <tr>
                                <td colSpan={10} style={{ background: '#f8fafc', padding: '1rem' }}>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' }}>
                                    {[
                                      { title: 'Отчёты', color: '#1e40af', items: [
                                        `Всего: ${emp.reports.total}`,
                                        `Утверждено: ${emp.reports.approved} (${emp.reports.approval_rate}%)`,
                                        `Вовремя: ${emp.reports.on_time} (${emp.reports.on_time_rate}%)`,
                                        `С опозданием: ${emp.reports.late}`,
                                      ], score: emp.reports.score },
                                      { title: 'Поручения', color: '#047857', items: [
                                        `Всего: ${emp.documents.total}`,
                                        `Выполнено: ${emp.documents.completed} (${emp.documents.completion_rate}%)`,
                                        `В срок: ${emp.documents.completed_on_time} (${emp.documents.on_time_rate}%)`,
                                        `Просрочено: ${emp.documents.overdue}`,
                                      ], score: emp.documents.score },
                                      { title: 'Задачи плана', color: '#7c3aed', items: [
                                        `Всего: ${emp.tasks.total}`,
                                        `Выполнено: ${emp.tasks.completed} (${emp.tasks.completion_rate}%)`,
                                        `Ср. выполнение: ${emp.tasks.avg_completion}%`,
                                        `Переносов: ${emp.tasks.carry_over}`,
                                      ], score: emp.tasks.score },
                                      { title: 'Посещаемость', color: '#b45309', items: [
                                        `На работе: ${emp.attendance.present} дн.`,
                                        `Отпуск: ${emp.attendance.vacation} дн.`,
                                        `Болезнь: ${emp.attendance.sick} дн.`,
                                        `Отсутствие: ${emp.attendance.absent} дн.`,
                                      ], score: emp.attendance.attendance_rate },
                                    ].map(block => (
                                      <div key={block.title} style={{ background: '#fff', borderRadius: '8px', padding: '0.75rem', borderTop: `3px solid ${block.color}` }}>
                                        <div style={{ fontWeight: 600, color: block.color, marginBottom: '0.5rem', fontSize: '0.85rem' }}>{block.title}</div>
                                        {block.items.map((item, i) => <div key={i} style={{ fontSize: '0.78rem', color: '#475569', marginBottom: '2px' }}>{item}</div>)}
                                        <div style={{ marginTop: '0.5rem', fontSize: '0.78rem' }}>
                                          Балл: <b style={{ color: kpiColor(block.score) }}>{block.score}</b>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Кнопки экспорта — показываются после загрузки данных */}
              {empKpiData && (
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => handleEmpKpiExport('excel')}
                    disabled={empKpiExporting !== null}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem',
                      background: empKpiExporting === 'excel' ? '#94a3b8' : 'linear-gradient(135deg, #16a34a, #15803d)',
                      color: '#fff', border: 'none',
                    }}
                  >
                    <Download size={15} />
                    {empKpiExporting === 'excel' ? 'Загрузка...' : '⬇ Excel'}
                  </button>
                  <button
                    onClick={() => handleEmpKpiExport('word')}
                    disabled={empKpiExporting !== null}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem',
                      background: empKpiExporting === 'word' ? '#94a3b8' : 'linear-gradient(135deg, #1e40af, #1e3a8a)',
                      color: '#fff', border: 'none',
                    }}
                  >
                    <Download size={15} />
                    {empKpiExporting === 'word' ? 'Загрузка...' : '⬇ Word'}
                  </button>
                </div>
              )}

              {!empKpiData && !empKpiLoading && !empKpiError && (
                <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem', background: '#f8fafc', borderRadius: '8px' }}>
                  Выберите параметры и нажмите «📊 Показать» для загрузки данных
                </div>
              )}
            </div>
          ) : (
            /* ── Стандартная панель экспорта ── */
            <div className="constructor-card">
              <h2>{t('reports:constructor.exportParams')}</h2>

              {/* Формат */}
              <div className="param-group">
                <label>{t('reports:constructor.fileFormat')}</label>
                <div className="format-options">
                  {currentSection ? currentSection.formats.map(f => (
                    <button
                      key={f}
                      className={`format-btn ${format === f ? 'active' : ''}`}
                      onClick={() => setFormat(f)}
                    >
                      {FORMAT_LABELS[f].label} (.{FORMAT_LABELS[f].ext})
                    </button>
                  )) : (
                    <span className="param-hint">{t('reports:constructor.selectSectionFirst')}</span>
                  )}
                </div>
              </div>

              {/* Год */}
              <div className="param-group">
                <label>{t('reports:constructor.year')}</label>
                <select
                  className="param-control"
                  value={year}
                  onChange={e => setYear(parseInt(e.target.value))}
                >
                  {years.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              {/* Отдел */}
              <div className="param-group">
                <label>{t('reports:constructor.department')}</label>
                <select
                  className="param-control"
                  value={department}
                  onChange={e => setDepartment(e.target.value)}
                >
                  <option value="">{t('reports:constructor.allDepartments')}</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.name_ru}</option>
                  ))}
                </select>
              </div>

              {/* Кнопка скачивания */}
              <button
                className="btn-download"
                onClick={handleDownload}
                disabled={!selectedSection || !format || downloading}
              >
                <Download size={18} />
                {downloading ? t('reports:constructor.downloading') : t('reports:constructor.downloadReport')}
              </button>
            </div>
          )}

          {/* Подсказка (только для не-employee_kpi секций) */}
          {currentSection && selectedSection !== 'employee_kpi' && (
            <div className="constructor-card preview-card">
              <h3>{t('reports:constructor.whatInFile')}</h3>
              <p>{currentSection.description}</p>
              <div className="preview-meta">
                <span>{t('reports:constructor.formatLabel')}: <strong>{FORMAT_LABELS[format]?.label}</strong></span>
                <span>{t('reports:constructor.yearLabel')}: <strong>{year}</strong></span>
                <span>{t('reports:constructor.departmentLabel')}: <strong>{department ? departments.find(d => String(d.id) === department)?.name_ru : t('reports:constructor.all')}</strong></span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReportConstructor;
